import { type VariantProp, cv } from "@suse-o11y/class-variance";
import { isFunction } from "@suse-o11y/is-function";
import { mapChildren } from "@suse-o11y/map-children";
import type { OneOrMany } from "@suse-o11y/one-or-many";
import { type Monitor, useMonitor } from "@suse-o11y/signal";
import type { SignalForm } from "@suse-o11y/signal/form";
import React from "react";

import type { PolymorphicBoxProps } from "~/components/polymorphic-box";

import { FormFieldContextProvider, useInitFormFieldContext } from "./_context";
import { FallbackErrorMessage } from "./_fallback-error-message";
import styles from "./_form.module.css";

const DEFAULT_ELEMENT = "div" satisfies React.ElementType;

const variants = cv(styles.formField, {
	variants: {
		layout: {
			horizontal: styles.horizontal,
			vertical: styles.vertical,
		},
	},

	defaultVariants: {
		layout: "vertical",
	},
});

type FormFieldLayout = VariantProp<typeof variants, "layout">;

type FormFieldRenderer<TForm extends SignalForm> = (monitor: Monitor, state: TForm) => React.ReactNode;

export interface FormFieldProps<TForm extends SignalForm> extends Omit<React.HTMLAttributes<HTMLElement>, "children"> {
	/**
	 * @default "vertical"
	 */
	layout?: FormFieldLayout;
	headless?: boolean;
	state: TForm;
	children: OneOrMany<React.ReactNode | FormFieldRenderer<TForm>>;
}

export const FormField = React.forwardRef(
	(
		{
			el: Root = DEFAULT_ELEMENT,
			layout,
			headless,
			state,
			className,
			children,
			...props
		}: PolymorphicBoxProps<React.ElementType, FormFieldProps<SignalForm>>,
		ref: React.ForwardedRef<HTMLDivElement>,
	) => {
		const monitor = useMonitor();
		const context = useInitFormFieldContext(state);

		const content = mapChildren(children, (child, index) => {
			if (!isFunction(child)) {
				return child;
			}

			const node = child(monitor, state);

			if (!React.isValidElement(node)) {
				return node;
			}

			// it is ok to assign index as the key here, because the children are not dynamic
			return <React.Fragment key={index}>{node}</React.Fragment>;
		});

		return (
			<Root className={headless ? className : variants({ layout, className })} ref={ref} {...props}>
				<FormFieldContextProvider value={context}>{content}</FormFieldContextProvider>

				{!headless && <FallbackErrorMessage errorMessageDescendants={context.errorMessageDescendants} state={state} />}
			</Root>
		);
	},
) as {
	<TForm extends SignalForm, TElement extends React.ElementType = typeof DEFAULT_ELEMENT>(
		props: PolymorphicBoxProps<TElement, FormFieldProps<TForm>>,
	): React.ReactElement;

	displayName?: string;
};

if (import.meta.env.DEV) {
	FormField.displayName = "FormField";
}
